import ButtonDisclosures from '../../../atoms/button-disclosures';
import ButtonSwitch from '../../../atoms/button-switch';
import CardModule from '../../shared/card-module';
import ChartContainer from '../../../atoms/chart-container';
import Label from '../../../atoms/label';
import TableBasic from '../../../atoms/table-basic';
import format from '../../../../utils/format';
import styles from './historical-rates-of-return.module.scss';
import useHistoricalRatesOfReturn from '../../../../stores/profile/modules/use-historical-rates-of-return';
import { augmentClassName } from '../../../../utils/GlobalCssProxy';
import { getTextValueColorClass } from '../../../../utils/sharedHelpers';
import PropTypes from 'prop-types';
import GrayText from '../../../atoms/grayText';

const className = augmentClassName(styles);

const HistoricalRatesOfReturn = ({
  chartName,
  hideSwitch,
  selectedPeriod,
  hideCumulativeReturns,
  isTraditional,
  isTIAAQdia
}) => {
  const {
    onChangePeriod,
    onResizeChart,
    periods,
    periodSelected,
    chart,
    disclosure,
    chartData,
    fundName,
    redemptionFee
  } = useHistoricalRatesOfReturn({ selectedPeriod });

  return (
    <CardModule
      data-analytics-componenttype="interactive-graphic-component"
      data-analytics-id="IG_HistoricalRatesOfReturn"
      data-analytics-section="component"
      {...className('historical-rates-of-return-module')}
    >
      <CardModule.Header>
        {!isTIAAQdia && (chartName || 'Historical Rates of Return')}
        <CardModule.AsOfDate>{format.date(chartData && chartData.dateAsOf)}</CardModule.AsOfDate>
        {hideSwitch ? undefined : (
          <div {...className('d-print-none')}>
            <ButtonSwitch options={periods} onChange={onChangePeriod} selected={periodSelected} />
          </div>
        )}
      </CardModule.Header>
      <div {...className('chart-table-scroll')}>
        <CardModule.Body>
          <div {...className('mb-5', 'd-print-none')}>
            <ChartContainer
              alt={`${periodSelected.label} performance returns chart`}
              src={chart}
              onResize={onResizeChart}
            />
          </div>
          <TableBasic {...className('category-header-row-label-table', 'fixed-left-column')}>
            <thead>
              <tr>
                <th role="presentation"></th>
                {hideCumulativeReturns ? undefined : (
                  <th {...className('header-category')} colSpan={3}>
                    <div>Cumulative Returns</div>
                  </th>
                )}
                <th
                  {...className(
                    'header-category',
                    hideCumulativeReturns ? undefined : 'border-left-3'
                  )}
                  colSpan={5}
                >
                  <div>Average Annualized Total Returns</div>
                </th>
              </tr>
              <tr>
                <th role="presentation"></th>
                {hideCumulativeReturns ? undefined : (
                  <>
                    <th {...className('border-dotted-left')}>1M</th>
                    <th {...className('border-dotted-left')}>3M</th>
                    <th {...className('border-dotted-left')}>YTD</th>
                  </>
                )}
                <th {...className(hideCumulativeReturns ? 'border-dotted-left' : 'border-left-3')}>
                  1Y
                </th>
                <th {...className('border-dotted-left')}>3Y</th>
                <th {...className('border-dotted-left')}>5Y</th>
                <th {...className('border-dotted-left')}>10Y</th>
                <th {...className('border-dotted-left')}>Since Inception</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <th scope="row" {...className('text-left')}>
                  <Label
                    labelColor={'chartBlue'}
                    labelText="This Investment"
                    // tooltip="To be determined by TIAA"
                  />
                  {fundName}
                </th>
                {chartData.bars.map((row, index) => {
                  const rowDataPoint = isTraditional
                    ? format.numberNoZeros(row.value, { formatString: '+0.00' })
                    : format.number(row.value, { formatString: '+0.00' });
                  return (
                    <td
                      data-key={`${row.label || ''}-${index}`}
                      key={`${row.label || ''}-${index}`}
                      {...className(
                        getTextValueColorClass(rowDataPoint),
                        index !== 3 ? 'border-dotted-left' : 'border-left-3'
                      )}
                    >
                      {rowDataPoint === '+0.00' ? '0.00' : rowDataPoint}
                    </td>
                  );
                })}
              </tr>
            </tbody>
          </TableBasic>
        </CardModule.Body>
      </div>
      <CardModule.Footer>
        {isTIAAQdia && redemptionFee && (
          <GrayText
            label={'Record Keeping Fee'}
            isString={true}
            value={`${redemptionFee}%`}
            grayTextDisclosure={
              'Performance is net of the recordkeeping fee that TIAA pays to the plan recordkeeper.'
            }
            textClass={'text-left'}
          />
        )}
        <div {...className('mt-1')}>
          <div {...className('mb-2', 'd-print-none')}>
            <ButtonDisclosures module={'historical-rates-of-return'} />
          </div>
          <p {...className('inline-disclosure-text', 'font-weight-bold', 'mt-2')}>{disclosure}</p>
        </div>
      </CardModule.Footer>
    </CardModule>
  );
};

HistoricalRatesOfReturn.propTypes = {
  chartName: PropTypes.string,
  hideSwitch: PropTypes.bool,
  hideCumulativeReturns: PropTypes.bool,
  isTIAAQdia: PropTypes.bool,
  isTraditional: PropTypes.bool,
  selectedPeriod: PropTypes.string
};

export default HistoricalRatesOfReturn;
